import React, { useCallback, useState } from "react";
import { Box, Divider, CircularProgress } from "@mui/material";
import {
  TrackButtonWrap,
  CustomButton,
  OrderInfoRow,
  OrderInfoLabel,
  OrderInfoValue,
  MapViewTrackingContainer,
  MapViewIconTitleBox,
  TrackingIconBox,
  MapViewTrackingTitle,
  MapViewTrackingDescription,
  MapViewOpenCaption,
  MapViewIntegratedOrderDetails,
  MapViewOrderDetailsTitle,
  MapViewOrderDetailsContainer,
} from "layout/styles";
import LaunchIcon from "@mui/icons-material/Launch";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import ReceiptIcon from "@mui/icons-material/Receipt";
import StorefrontIcon from "@mui/icons-material/Storefront";

const LiveTrackingView = ({
  trackUrl,
  orderInfo = {},
  queryOcoExternalId,
  showOrderDetails = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = useCallback(() => {
    if (!trackUrl || isLoading) {
      console.error("No tracking URL available or already loading");
      return;
    }

    setIsLoading(true);

    // Enhanced React Native WebView detection
    const isReactNativeWebView =
      typeof window.ReactNativeWebView !== "undefined";

    // Additional debugging for React Native detection
    console.log("WebView Detection:", {
      hasReactNativeWebView: isReactNativeWebView,
      windowKeys: Object.keys(window),
      userAgent: navigator.userAgent,
      isReactNative: typeof window.ReactNativeWebView !== "undefined",
    });

    if (isReactNativeWebView) {
      console.log("✅ Detected React Native WebView environment");

      // Send message to React Native app with tracking URL
      const message = {
        type: "NAVIGATE_TO_TRACKING",
        trackingUrl: trackUrl,
        timestamp: Date.now(),
      };

      console.log("Sending message to React Native:", message);

      window.ReactNativeWebView.postMessage(JSON.stringify(message));

      // Reset loading after a brief delay for React Native
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    } else {
      window.postMessage(JSON.stringify({ url: trackUrl }), "*");
      if (window.location.hash) {
        window.open(`${trackUrl}${window.location.hash}`, "_blank");
      } else {
        window.open(trackUrl, "_blank");
      }
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    }
  }, [trackUrl, isLoading]);

  return (
    <TrackButtonWrap>
      <MapViewTrackingContainer>
        {/* Icon and Title */}
        <MapViewIconTitleBox>
          <TrackingIconBox>
            <TrackChangesIcon sx={{ fontSize: 40, color: "white" }} />
          </TrackingIconBox>

          <MapViewTrackingTitle variant="h4">
            Live Tracking Available
          </MapViewTrackingTitle>

          <MapViewTrackingDescription variant="body1">
            Track your order in real-time with our delivery partner's tracking
            system
          </MapViewTrackingDescription>
        </MapViewIconTitleBox>

        {/* Enhanced Button */}
        <CustomButton
          variant="contained"
          sx={[
            isLoading && {
              opacity: 0.5,
              cursor: "not-allowed",
            },
          ]}
          onClick={handleClick}
          disabled={!trackUrl || isLoading}
          startIcon={<LocationOnIcon />}
          endIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <LaunchIcon />
            )
          }
        >
          Show Live Tracking (Beta)
        </CustomButton>

        {/* Additional Info */}
        <MapViewOpenCaption variant="caption">
          <LaunchIcon sx={{ fontSize: 14 }} />
          Opens in new window
        </MapViewOpenCaption>

        {/* Order Details Section - Integrated when showOrderDetails is true */}
        {showOrderDetails && (
          <MapViewIntegratedOrderDetails>
            <MapViewOrderDetailsTitle variant="h6">
              Order Details
            </MapViewOrderDetailsTitle>

            <MapViewOrderDetailsContainer>
              <OrderInfoRow>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ReceiptIcon sx={{ color: "primary.main", fontSize: 18 }} />
                  <OrderInfoLabel>Order Number</OrderInfoLabel>
                </Box>
                <OrderInfoValue data-type="order-number">
                  #{orderInfo?.number || "N/A"}
                </OrderInfoValue>
              </OrderInfoRow>

              <Divider sx={{ my: 0.5 }} />

              <OrderInfoRow>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <StorefrontIcon
                    sx={{ color: "primary.main", fontSize: 18 }}
                  />
                  <OrderInfoLabel>Restaurant</OrderInfoLabel>
                </Box>
                <OrderInfoValue>
                  {orderInfo?.companyName || "N/A"}
                </OrderInfoValue>
              </OrderInfoRow>

              <Divider sx={{ my: 0.5 }} />

              <OrderInfoRow>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationOnIcon
                    sx={{ color: "primary.main", fontSize: 18 }}
                  />
                  <OrderInfoLabel>Delivery Address</OrderInfoLabel>
                </Box>
                <OrderInfoValue
                  sx={{
                    maxWidth: "300px",
                    fontSize: "16px",
                  }}
                >
                  {orderInfo?.address || "N/A"}
                </OrderInfoValue>
              </OrderInfoRow>
            </MapViewOrderDetailsContainer>
          </MapViewIntegratedOrderDetails>
        )}
      </MapViewTrackingContainer>
    </TrackButtonWrap>
  );
};

export default LiveTrackingView;
